import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useFacility } from '../context/FacilityContext';
import { useAuth } from '../context/AuthContext';
import facilitiesService from '../services/facilitiesService';
import apiClient from '../utils/apiClient';
import { toast } from 'sonner';

const schema = z.object({
  name: z.string().min(1, 'Required'),
  website_domain: z.string().optional(),
  // Operator-set position in the facility picker dropdown. Empty = unset
  // (falls back to creation order). Coerced from the text input; '' maps to
  // null so clearing it removes the override.
  display_order: z
    .union([z.coerce.number().int().min(0), z.literal('')])
    .optional(),
  // Marks this facility as the one the dashboard opens to on sign-in. At most
  // one per org is default (server enforces); the checkbox is a plain boolean.
  is_default_landing: z.boolean().optional(),
  address: z.string().min(1, 'Required'),
  city: z.string().min(1, 'Required'),
  state: z.string().min(1, 'Required'),
  zip_code: z.string().min(1, 'Required'),
  mailing_address: z.string().optional(),
  mailing_city: z.string().optional(),
  mailing_state: z.string().optional(),
  mailing_zip_code: z.string().optional(),
  phone: z.string().optional(),
  vapi_phone_number: z.string().optional(),
  voice_forward_number: z.string().optional(),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  timezone: z.string().min(1),
  notification_send_hour: z.number().int().min(0).max(23).nullable().optional(),
  gate_codes_enabled: z.boolean().optional(),
  access_hours_mode: z.enum(['', '24h', 'custom']).optional(),
  access_hours_start: z.string().optional(),
  access_hours_end: z.string().optional(),
  review_link: z.string().optional(),
  require_lease_signing: z.boolean().optional(),
  tax_rate: z.coerce.number().min(0).max(100),
  invoice_generation_day: z.coerce.number().int().min(1).max(28),
  brownfield: z.boolean().optional(),
  proration_mode_customer: z.enum([
    'none',
    'first_full_prorate_next_now',
    'first_full_prorate_next_later',
    'prorate_now_full_next',
    'prorate_now_plus_first_full',
  ]),
  stripe_account_id: z.string().optional(),
});

const PRORATION_OPTIONS = [
  { value: 'first_full_prorate_next_now',
    label: 'Charge first full month + prorated next month at signup' },
  { value: 'first_full_prorate_next_later',
    label: 'Charge first full month at signup; prorate second month on next cycle' },
  { value: 'prorate_now_full_next',
    label: 'Charge prorated first month at signup; full second month on next cycle' },
  { value: 'prorate_now_plus_first_full',
    label: 'Charge prorated current + first full month at signup; cycle from the 1st' },
  { value: 'none', label: 'No proration — bill on each rental’s anniversary day' },
];

const Field = ({ label, error, children }) => (
  <div>
    {label && (
      <label className="block text-[12px] font-medium text-slate-500 mb-1">{label}</label>
    )}
    {children}
    {error && <p className="text-[12px] text-red-500 mt-1">{error.message}</p>}
  </div>
);

const inputCls =
  'w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500';

const FacilityForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentFacility, refreshFacilities } = useFacility();
  const { isPortfolioScoped, isPlatformUser } = useAuth();
  // Only org-wide roles (portfolio owner/manager) — and platform users, who
  // manage every org during impersonation — may set the org-wide picker
  // controls: dropdown order AND the default landing facility. Hidden for
  // facility-scoped roles (facility owner/manager, staff, viewer).
  const canSetOrgPickerControls = isPortfolioScoped() || isPlatformUser();
  // CRITICAL: edit-vs-create is decided by the ROUTE, never by "is a
  // facility currently selected". The old `Boolean(currentFacility?._id)`
  // meant that with any facility selected, creating a NEW facility instead
  // PUT-overwrote the selected one (data loss — a second facility replaced
  // the first). /settings/facility/new always creates; /settings/facility
  // edits the current one.
  const isCreate = location.pathname.endsWith('/new');
  const isEdit = !isCreate && Boolean(currentFacility?._id);
  const [serverError, setServerError] = useState(null);
  const [saving, setSaving] = useState(false);
  // Late-fee start date lives in late_lien_settings, not on the facility, so
  // it is loaded and saved separately from the react-hook-form payload. It
  // belongs on this tab because it is a migration decision: without it, the
  // first run after switching late fees on charges every tier on every
  // already-late invoice at once.
  const [lateFeeStart, setLateFeeStart] = useState('');
  const [lateFeeStartLoaded, setLateFeeStartLoaded] = useState('');
  // Three tabs. The form is ONE <form> — tabs only show/hide sections, so a
  // save always submits every field regardless of which tab is open. (Hiding
  // with CSS rather than unmounting matters: unmounted inputs would drop out
  // of the payload and silently blank stored values.)
  const [section, setSection] = useState('general');
  const SECTIONS = [
    { key: 'general', label: 'General' },
    { key: 'billing', label: 'Billing & Payments' },
    { key: 'lifecycle', label: 'Migration from another system (brownfield)' },
  ];

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      website_domain: '',
      display_order: '',
      is_default_landing: false,
      address: '',
      city: '',
      state: '',
      zip_code: '',
      mailing_address: '',
      mailing_city: '',
      mailing_state: '',
      mailing_zip_code: '',
      phone: '',
      vapi_phone_number: '',
      voice_forward_number: '',
      email: '',
      timezone: 'America/Chicago',
      notification_send_hour: null,
      gate_codes_enabled: true,
      access_hours_mode: '',
      access_hours_start: '06:00',
      access_hours_end: '21:00',
      review_link: '',
      require_lease_signing: true,
      tax_rate: 0,
      invoice_generation_day: 24,
      brownfield: false,
      proration_mode_customer: 'first_full_prorate_next_now',
      stripe_account_id: '',
    },
  });

  useEffect(() => {
    // In create mode, never prefill from the selected facility — that is
    // exactly how the old bug leaked one facility's data into a "new" one.
    if (currentFacility && !isCreate) {
      reset({
        name: currentFacility.name || '',
        website_domain: currentFacility.website_domain || '',
        display_order:
          currentFacility.display_order == null
            ? ''
            : currentFacility.display_order,
        is_default_landing: !!currentFacility.is_default_landing,
        address: currentFacility.address || '',
        city: currentFacility.city || '',
        state: currentFacility.state || '',
        zip_code: currentFacility.zip_code || '',
        mailing_address: currentFacility.mailing_address || '',
        mailing_city: currentFacility.mailing_city || '',
        mailing_state: currentFacility.mailing_state || '',
        mailing_zip_code: currentFacility.mailing_zip_code || '',
        phone: currentFacility.phone || '',
        vapi_phone_number: currentFacility.vapi_phone_number || '',
        voice_forward_number: currentFacility.voice_forward_number || '',
        email: currentFacility.email || '',
        timezone: currentFacility.timezone || 'America/Chicago',
        notification_send_hour: currentFacility.notification_send_hour ?? null,
        // Reflect today's EFFECTIVE behavior: explicit choice wins; a
        // facility that never chose shows what the engine currently infers
        // (gate config exists = gated). Saving makes it explicit.
        gate_codes_enabled:
          currentFacility.gate_system?.codes_enabled ??
          Boolean(currentFacility.gate_system),
        access_hours_mode: currentFacility.access_hours_mode
          ?? (/24/.test(currentFacility.access_hours || '') ? '24h' : ''),
        access_hours_start: currentFacility.access_hours_start || '06:00',
        access_hours_end: currentFacility.access_hours_end || '21:00',
        review_link: currentFacility.review_link || '',
        // Absent means ON — matches the backend/portal reading of this flag
        // (`!== false`). Only an explicit false turns the gate off.
        require_lease_signing: currentFacility.require_lease_signing !== false,
        tax_rate: currentFacility.tax_rate ?? 0,
        invoice_generation_day: currentFacility.invoice_generation_day ?? 24,
        brownfield: currentFacility.brownfield ?? false,
        proration_mode_customer:
          currentFacility.proration_mode_customer || 'first_full_prorate_next_now',
        stripe_account_id: currentFacility.stripe_account_id || '',
      });
    }
  }, [currentFacility, reset, isCreate]);

  // Load the late-fee start date for the facility being edited.
  useEffect(() => {
    if (isCreate || !currentFacility?._id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get(
          `/api/facilities/${currentFacility._id}/fees/late-lien`);
        if (cancelled) return;
        const v = (res.data?.assess_from_date || '').slice(0, 10);
        setLateFeeStart(v);
        setLateFeeStartLoaded(v);
      } catch {
        // A missing late/lien config is normal on a new facility — leave the
        // field blank rather than blocking the whole form.
      }
    })();
    return () => { cancelled = true; };
  }, [currentFacility?._id, isCreate]);

  const onSubmit = async (data) => {
    setSaving(true);
    setServerError(null);
    try {
      const payload = { ...data };
      // Gate-codes checkbox lives inside facility.gate_system — merge
      // into the EXISTING block (the Gate drawer owns provider config;
      // a bare object would clobber it). Every save records the choice
      // explicitly.
      payload.gate_system = {
        ...(currentFacility?.gate_system || {}),
        codes_enabled: !!payload.gate_codes_enabled,
      };
      delete payload.gate_codes_enabled;
      // Access hours: derive the display string tenants see in email from
      // the structured choice. '' = leave whatever prose is stored.
      const fmt12 = (hhmm) => {
        const [h, m] = String(hhmm || '').split(':').map(Number);
        if (Number.isNaN(h)) return hhmm;
        const ampm = h < 12 ? 'AM' : 'PM';
        const h12 = h % 12 === 0 ? 12 : h % 12;
        return `${h12}:${String(m || 0).padStart(2, '0')} ${ampm}`;
      };
      if (payload.access_hours_mode === '24h') {
        payload.access_hours = '24-hour access';
      } else if (payload.access_hours_mode === 'custom') {
        payload.access_hours = `${fmt12(payload.access_hours_start)} – ${fmt12(payload.access_hours_end)} daily`;
      }
      if (payload.access_hours_mode === '') {
        delete payload.access_hours_mode;
        delete payload.access_hours_start;
        delete payload.access_hours_end;
      }
      // Normalize dropdown position: '' -> null (clear the override). Drop the
      // org-wide picker fields entirely for roles that can't set them, so they
      // never push a value (server strips them too, but don't even send).
      if (!canSetOrgPickerControls) {
        delete payload.display_order;
        delete payload.is_default_landing;
      } else if (payload.display_order === '' || payload.display_order == null) {
        payload.display_order = null;
      }
      if (isEdit) {
        await facilitiesService.update(currentFacility._id, payload);
        // Separate collection, so a separate call — and only when it changed,
        // to avoid rewriting late/lien config on every facility save.
        if (lateFeeStart !== lateFeeStartLoaded) {
          await apiClient.put(
            `/api/facilities/${currentFacility._id}/fees/late-lien`,
            { assess_from_date: lateFeeStart || null });
          setLateFeeStartLoaded(lateFeeStart);
        }
      } else {
        await facilitiesService.create(payload);
      }
      await refreshFacilities();
      // Saving an edit keeps the operator on the page they were changing;
      // only a newly created facility returns to the list.
      if (isEdit) {
        toast.success('Settings saved.');
      } else {
        navigate('/settings');
      }
    } catch (err) {
      setServerError(err.response?.data?.detail || 'Could not save facility.');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!isEdit || !currentFacility?._id) return;
    setSaving(true);
    setServerError(null);

    // Step 1: fetch the cascade preview so we can warn the operator
    // exactly what will be wiped.
    let preview;
    try {
      const res = await facilitiesService.deletePreview(currentFacility._id);
      preview = res.data;
    } catch (err) {
      setSaving(false);
      setServerError(err.response?.data?.detail || 'Could not load delete preview.');
      return;
    }

    if (!preview.can_delete) {
      setSaving(false);
      setServerError(
        `Cannot delete: ${preview.rentals_active} active rental(s) exist. End them first.`,
      );
      return;
    }

    // Step 2: show a single confirmation listing the cascade counts.
    const lines = [
      `This will PERMANENTLY delete "${currentFacility.name}" and ALL its data:`,
      '',
      `  • ${preview.units} unit(s)`,
      `  • ${preview.unit_types} unit type(s)`,
      `  • ${preview.customers} customer(s)`,
      `  • ${preview.rentals_total} rental(s) (none active)`,
      `  • ${preview.leads} lead(s)`,
      `  • ${preview.invoices} invoice(s)`,
      `  • ${preview.payments} payment(s)`,
      `  • ${preview.site_maps} site-map layout(s)`,
      '',
      'This cannot be undone.',
      '',
      `Type the facility name (${currentFacility.name}) to confirm:`,
    ];
    const typed = window.prompt(lines.join('\n'));
    if (typed !== currentFacility.name) {
      setSaving(false);
      if (typed !== null) setServerError('Name did not match — not deleted.');
      return;
    }

    // Step 3: actually delete.
    try {
      await facilitiesService.delete(currentFacility._id);
      await refreshFacilities();
      navigate('/settings');
    } catch (err) {
      setServerError(
        err.response?.data?.detail || 'Could not delete facility.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-slate-900">
          {isEdit ? 'Facility Settings' : 'Create Facility'}
        </h1>
        <p className="text-[13px] text-slate-400 mt-0.5">
          {isEdit
            ? 'Update this facility’s details, tax, late fees, and payment account.'
            : 'Set up your storage facility to start managing units and rentals.'}
        </p>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="rounded-xl border border-slate-200/80 bg-white p-6 space-y-4"
      >
        <div className="border-b border-slate-200 mb-5">
          <nav className="-mb-px flex gap-6" aria-label="Facility settings sections">
            {SECTIONS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setSection(t.key)}
                className={`whitespace-nowrap py-2.5 px-1 border-b-2 text-[13px] font-medium transition-colors ${
                  section === t.key
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>

        <div className={section === 'general' ? 'space-y-4' : 'hidden'}>
        <h2 className="text-[14px] font-semibold text-slate-900 -mb-1">General</h2>
        <Field label="Facility name" error={errors.name}>
          <input className={inputCls} {...register('name')} />
        </Field>

        <Field label="Website" error={errors.website_domain}>
          <input
            className={inputCls}
            placeholder="example.com"
            {...register('website_domain')}
          />
        </Field>

        <Field label="Timezone" error={errors.timezone}>
          <select className={inputCls} {...register('timezone')}>
            <option value="America/New_York">Eastern — America/New_York</option>
            <option value="America/Chicago">Central — America/Chicago</option>
            <option value="America/Denver">Mountain — America/Denver</option>
            <option value="America/Phoenix">Arizona (no DST) — America/Phoenix</option>
            <option value="America/Los_Angeles">Pacific — America/Los_Angeles</option>
            <option value="America/Anchorage">Alaska — America/Anchorage</option>
            <option value="Pacific/Honolulu">Hawaii — Pacific/Honolulu</option>
          </select>
          <p className="text-[12px] text-slate-400 mt-1">
            All dates and times for this facility — texts, invoices, reports —
            display in this timezone.
          </p>
        </Field>

        <Field label="Daily send time" error={errors.notification_send_hour}>
          {/* "" = not set, which the backend reads as the 10:00 default. It
              is a distinct option rather than silently rendering 10, because
              showing a stored-looking value for an absent field made it
              impossible to tell a deliberate 10:00 from a facility that had
              never been configured. */}
          <select
            className={inputCls}
            {...register('notification_send_hour', {
              setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
            })}
          >
            <option value="">Default (10:00 AM)</option>
            {Array.from({ length: 24 }, (_, h) => (
              <option key={h} value={h}>
                {h === 0 ? '12:00 AM' : h < 12 ? `${h}:00 AM` : h === 12 ? '12:00 PM' : `${h - 12}:00 PM`}
              </option>
            ))}
          </select>
          <p className="text-[12px] text-slate-400 mt-1">
            The local hour scheduled sends and autopay charges run —
            invoices, rent reminders, and late notices. Late FEES post at
            midnight on their tier day; only the notice about them waits
            for this hour. Immediate emails (move-in confirmations,
            receipts, sign-in codes) are never held.
          </p>
        </Field>

        <Field label="Gate" error={errors.gate_codes_enabled}>
          <label className="flex items-center gap-2 text-[13px] text-slate-700">
            <input type="checkbox" {...register('gate_codes_enabled')} />
            This facility has a gate with access codes
          </label>
          <p className="text-[12px] text-slate-400 mt-1">
            Checked: new tenants get the move-in email WITH their gate code.
            Unchecked: they get the no-code version and codes are never
            created. Code style and gate provider live in Settings &rarr;
            Communication &rarr; Gate.
          </p>
        </Field>

        <Field label="Facility access hours" error={errors.access_hours_mode}>
          <select className={inputCls} {...register('access_hours_mode')}>
            <option value="">Not shown in emails</option>
            <option value="24h">24-hour access</option>
            <option value="custom">Set hours (from / to)</option>
          </select>
          {watch('access_hours_mode') === 'custom' && (
            <div className="flex items-center gap-2 mt-2">
              <input type="time" className={inputCls}
                     {...register('access_hours_start')} />
              <span className="text-[13px] text-slate-400">to</span>
              <input type="time" className={inputCls}
                     {...register('access_hours_end')} />
            </div>
          )}
          <p className="text-[12px] text-slate-400 mt-1">
            Shown as the "Facility Access Hours" line in move-in emails.
            Lockout terms live in the lease, not in email.
          </p>
        </Field>

        <Field label="Review link" error={errors.review_link}>
          <input
            className={inputCls}
            placeholder="https://g.page/r/…/review"
            {...register('review_link')}
          />
          <p className="text-[12px] text-slate-400 mt-1">
            Your public review URL (e.g. Google review link) — merged into
            marketing-engine messages as {'{{review_link}}'}.
          </p>
        </Field>



        {canSetOrgPickerControls && (
          <>
            <Field label="Dropdown position" error={errors.display_order}>
              <input
                type="number"
                min="0"
                step="1"
                className={inputCls}
                placeholder="Leave blank for default order"
                {...register('display_order')}
              />
              <p className="text-[12px] text-slate-400 mt-1">
                Sets where this facility appears in the facility picker when
                you manage more than one. Lower numbers show first. Leave
                blank to use the default (creation) order. Only portfolio
                owners and managers can set this.
              </p>
            </Field>

            <Field error={errors.is_default_landing}>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  {...register('is_default_landing')}
                />
                <span>
                  <span className="block text-[13px] font-medium text-slate-700">
                    Flagship facility — open here when I sign in
                  </span>
                  <span className="block text-[12px] text-slate-400 mt-0.5">
                    The dashboard opens to this facility on a fresh sign-in.
                    Only one facility can be the flagship — choosing this one
                    clears it from any other.
                  </span>
                </span>
              </label>
            </Field>
          </>
        )}

        <div className="pt-4 mt-2 border-t border-slate-100">
          <h2 className="text-[14px] font-semibold text-slate-900 mb-3">
            Physical Address
          </h2>
          <Field label="Address" error={errors.address}>
            <input className={inputCls} {...register('address')} />
          </Field>

          <div className="grid grid-cols-3 gap-3 mt-3">
            <Field label="City" error={errors.city}>
              <input className={inputCls} {...register('city')} />
            </Field>
            <Field label="State" error={errors.state}>
              <input className={inputCls} {...register('state')} />
            </Field>
            <Field label="ZIP" error={errors.zip_code}>
              <input className={inputCls} {...register('zip_code')} />
            </Field>
          </div>
        </div>

        <div className="pt-4 mt-2 border-t border-slate-100">
          <h2 className="text-[14px] font-semibold text-slate-900 mb-1">
            Billing Address
          </h2>
          <p className="text-[12px] text-slate-400 mb-3">
            Where tenants remit payment. Leave blank to use the physical address.
          </p>
          <Field label="Address" error={errors.mailing_address}>
            <input className={inputCls} {...register('mailing_address')} />
          </Field>

          <div className="grid grid-cols-3 gap-3 mt-3">
            <Field label="City" error={errors.mailing_city}>
              <input className={inputCls} {...register('mailing_city')} />
            </Field>
            <Field label="State" error={errors.mailing_state}>
              <input className={inputCls} {...register('mailing_state')} />
            </Field>
            <Field label="ZIP" error={errors.mailing_zip_code}>
              <input className={inputCls} {...register('mailing_zip_code')} />
            </Field>
          </div>
        </div>

        <div className="pt-4 mt-2 border-t border-slate-100">
          <h2 className="text-[14px] font-semibold text-slate-900 mb-3">Phone</h2>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone" error={errors.phone}>
              <input className={inputCls} {...register('phone')} />
            </Field>
            <Field label="Email" error={errors.email}>
              <input className={inputCls} {...register('email')} />
            </Field>
          </div>
        </div>

        <Field label="AI agent phone number" error={errors.vapi_phone_number}>
          <input
            className={inputCls}
            placeholder="+19035551234"
            {...register('vapi_phone_number')}
          />
          <p className="text-[12px] text-slate-400 mt-1">
            The Vapi/telephony number that routes inbound calls to this
            facility's AI voice agent. Calls to this number create leads
            here automatically. Leave blank if not using the voice agent.
          </p>
        </Field>

        <Field label="AI agent forward number" error={errors.voice_forward_number}>
          <input
            className={inputCls}
            placeholder="+19035551234"
            {...register('voice_forward_number')}
          />
          <p className="text-[12px] text-slate-400 mt-1">
            Where the voice agent transfers a live call when it needs a
            human (e.g., complex question, caller asks for staff). The
            agent reads this at call-time, so updates take effect on the
            next call. Leave blank to disable live transfers.
          </p>
        </Field>

        {/* Late fee / Late after used to live here as facility fields. They
            were DEAD and actively misleading: run_late_lien charges from
            late_lien_settings.tiers (Settings → Fees), never from these, so a
            facility could display "$30 after 5 days" while actually charging
            $25 on day 5 and $25 more on day 10. Removed rather than synced —
            one source of truth for a money value. */}
        <div className="grid grid-cols-3 gap-3">
          <Field label="Tax rate (%)" error={errors.tax_rate}>
            <input type="number" step="0.01" className={inputCls} {...register('tax_rate')} />
          </Field>
          <div className="col-span-2 self-end pb-2">
            <p className="text-[12px] text-slate-500">
              Late fees are set per tier in{' '}
              <Link to="/settings/fees?tab=late-lien" className="text-blue-600 hover:text-blue-700 font-medium">
                Settings → Fees
              </Link>
              {' '}(escalation schedule), so the amount and the day it applies
              always match what tenants are charged.
            </p>
          </div>
        </div>

        </div>

        <div className={section === 'billing' ? 'space-y-4' : 'hidden'}>
        <div className="pt-1">
          <h2 className="text-[14px] font-semibold text-slate-900 mb-3">
            Billing &amp; Proration
          </h2>

          <Field
            label="Invoice generation day (1-28)"
            error={errors.invoice_generation_day}
          >
            <input
              type="number"
              min="1"
              max="28"
              className={inputCls}
              {...register('invoice_generation_day')}
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Day of month the cron generates invoices for the 1st of the
              following month + fires the reminder email. Default = 24
              (Apr 24 emails an invoice for May 1). Capped at 28 to
              dodge February edge cases.
            </p>
          </Field>


          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <Field
              label="Proration mode — Customer (Rent Now)"
              error={errors.proration_mode_customer}
            >
              <select
                className={inputCls}
                {...register('proration_mode_customer')}
              >
                {PRORATION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>
          </div>
          <p className="text-[11px] text-slate-400 mt-1">
            Controls how the move-in charge is broken up between the
            current and next billing cycle — applies to every move-in
            (website and operator-initiated alike).
          </p>
        </div>

        <Field label="Stripe connected account ID (optional)" error={errors.stripe_account_id}>
          <input className={inputCls} placeholder="acct_..." {...register('stripe_account_id')} />
          <p className="text-[11px] text-slate-400 mt-1">
            Required before tenants can pay online. Leave blank for cash/check only.
          </p>
        </Field>

        </div>

        {/* Both switches decide what a MIGRATED tenant experiences, which is
            why they sit together and away from routine billing fields. Each
            one can silence or gate part of the tenant lifecycle, so the copy
            spells out what actually happens rather than naming the flag. */}
        <div className={section === 'lifecycle' ? 'space-y-3' : 'hidden'}>
          <p className="text-[12px] text-slate-500">
            These settings only matter when you take over a facility that
            already has tenants. For a brand-new site the defaults below are
            correct — leave them as they are.
          </p>

          <h3 className="text-[13px] font-semibold text-slate-800 pt-2">
            1. Do you want to migrate tenants to your lease, or keep their old one?
          </h3>

        <Field label="Require lease signature before paying">
          <label className="flex items-center gap-2 text-[13px] text-slate-700 select-none">
            <input type="checkbox" className="rounded border-slate-300" {...register('require_lease_signing')} />
            Tenants must sign their lease before the portal lets them pay
          </label>
          <ul className="text-[12px] text-slate-500 mt-2 space-y-2 list-disc pl-4">
            <li>
              <span className="font-medium text-slate-700">Checked (default) —
              everyone signs your lease.</span> A tenant without a signed
              mySMS360 lease sees a “Sign lease” prompt and a greyed-out Pay
              button until they sign. Common after buying a facility: you
              inherit the tenants, not the old owner's paperwork.
            </li>
            <li>
              <span className="font-medium text-slate-700">Unchecked — you honour
              the leases they already signed.</span> Nobody is prompted, nobody
              is blocked. Keep each old lease by uploading the scan on that
              tenant's <span className="font-medium text-slate-700">customer
              page → Documents</span>.
            </li>
            <li className="text-slate-400">
              This setting only governs tenants who came from your old system.
              A brand-new rental always signs first either way — online, the
              rent wizard will not take payment until the lease is signed; at
              the counter, the rental is held as a draft and the tenant is
              emailed a signing link. A payment you record for a tenant
              yourself is never blocked.
            </li>
          </ul>
        </Field>

        <h3 className="text-[13px] font-semibold text-slate-800 pt-5 mt-4 border-t border-slate-100">
          2. Mute tenant communications while you migrate?
        </h3>

        <Field error={errors.brownfield}>
          <label className="flex items-start gap-2 cursor-pointer mt-2">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              {...register('brownfield')}
            />
            <span>
              <span className="block text-[13px] font-medium text-slate-700">
                Mute all tenant communications during migration
              </span>
              <span className="block text-[12px] text-slate-500 mt-1">
                <span className="block mb-1.5">
                  Unchecked by default — correct for a new site.{' '}
                  <span className="font-medium text-slate-700">Uncheck it
                  before your first automated billing run.</span>
                </span>
                <ul className="space-y-1.5 list-disc pl-4">
                  <li>
                    <span className="font-medium text-slate-700">Checked —
                    imported tenants hear nothing from mySMS360.</span> It
                    generates no invoices, reminders, late fees, emails or
                    texts for them, so an imported balance or paid-through
                    date that isn't right yet can't reach anyone. They carry on
                    being billed wherever they are billed today.
                  </li>
                  <li>
                    Tenants who rent through your website are billed and
                    notified as normal, so you can watch a real move-in run end
                    to end first.
                  </li>
                  <li>
                    <span className="font-medium text-slate-700">Uncheck once
                    the numbers look right.</span> mySMS360 takes over billing
                    and notifying them — that is the point you stop billing
                    them anywhere else.
                  </li>
                  <li className="text-slate-400">
                    Applies to all imported tenants at once and takes effect on
                    the next billing run — uncheck just after a run so you see
                    the invoices before your tenants do.
                  </li>
                </ul>
              </span>
            </span>
          </label>
        </Field>

        {isEdit && (
          <>
            <h3 className="text-[13px] font-semibold text-slate-800 pt-5 mt-4 border-t border-slate-100">
              3. From when should late fees be charged?
            </h3>

            <Field label="Charge late fees on rent due on or after">
              <input
                type="date"
                className={inputCls + ' max-w-[220px]'}
                value={lateFeeStart}
                onChange={(e) => setLateFeeStart(e.target.value)}
              />
              <ul className="text-[12px] text-slate-500 mt-2 space-y-2 list-disc pl-4">
                <li>
                  <span className="font-medium text-slate-700">Rent that was
                  already due before this date never gets a late fee</span> —
                  not now, not later. Everything from this date onward is
                  charged normally on your usual schedule.
                </li>
                <li>
                  <span className="font-medium text-slate-700">Set this before
                  you switch late fees on.</span> Fees apply to anything past
                  its grace period, so without a start date the first run
                  charges every overdue month a tenant has ever had, all at
                  once. Pick the first month you actually want to charge for —
                  usually the first full month after your cutover.
                </li>
                <li>
                  Leave it empty to charge on every overdue invoice regardless
                  of age.
                </li>
                <li className="text-slate-400">
                  This waives the fee only. A tenant behind on rent is still
                  marked delinquent and still loses gate access on your usual
                  day — you are forgiving the penalty, not the rent.
                </li>
              </ul>
            </Field>
          </>
        )}
        </div>

        {serverError && <p className="text-[13px] text-red-500">{serverError}</p>}

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-blue-600 text-white text-[13px] font-medium px-5 py-2 hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create facility'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/settings')}
            className="text-[13px] text-slate-500 hover:text-slate-700"
          >
            Cancel
          </button>
        </div>

        {isEdit && (
          <div className="mt-8 pt-5 border-t border-rose-100">
            <p className="text-[13px] font-medium text-rose-700 mb-1">
              Danger zone
            </p>
            <p className="text-[12px] text-slate-500 mb-3">
              Permanently delete this facility. Blocked if it has active
              rentals. This cannot be undone.
            </p>
            <button
              type="button"
              onClick={onDelete}
              disabled={saving}
              className="rounded-lg border border-rose-200 text-rose-700 text-[13px] font-medium px-4 py-2 hover:bg-rose-50 disabled:opacity-50 transition-colors"
            >
              Delete this facility
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default FacilityForm;
